import { useCallback, useEffect, useMemo, useRef } from "react";

import { trackError } from "app/pages/sales-intelligence/utils/errors/tracking";
import { SiError } from "app/pages/sales-intelligence/utils/errors/types";

import useImpactReportQuery from "../queries/useImpactReportQuery";
import { useIndustryTransitionEnrollMutation } from "../queries/useIndustryTransitionEnrollMutation";
import industryTrackingService from "../services/tracking/trackingService";
import { useIndustryTransitionModalStore } from "../store/useIndustryTransitionModalStore";
import type { IndustryTransitionImpactReport } from "../types/impactReport";
import { impactReportPhaseMap } from "../types/impactReportPhase";
import type { ImpactReportPhase } from "../types/impactReportPhase";
import { toVisibleImpactReport } from "../utils/toVisibleImpactReport";

type IndustryTransitionImpactReportModal = {
    readonly phase: ImpactReportPhase;
    readonly report: IndustryTransitionImpactReport | undefined;
    readonly canEnroll: boolean;
    readonly canRetryEnroll: boolean;
    readonly handlers: {
        readonly enroll: () => void;
        readonly retryEnroll: () => void;
        readonly retryReport: () => Promise<unknown>;
        readonly close: () => void;
    };
};

/**
 * Everything the modal needs except which tab is open — that lives inside the body, because state
 * above `<Modal>` re-renders react-aria's overlay, which measures `document.body` during render.
 */
export const useIndustryTransitionImpactReportModal = (): IndustryTransitionImpactReportModal => {
    const closeStore = useIndustryTransitionModalStore((state) => state.close);
    const markEnrollRetryUsed = useIndustryTransitionModalStore((state) => state.markEnrollRetryUsed);
    const hasUsedEnrollRetry = useIndustryTransitionModalStore((state) => state.hasUsedEnrollRetry);

    const reportQuery = useImpactReportQuery();
    const enroll = useIndustryTransitionEnrollMutation();

    const report = useMemo(
        () => (reportQuery.data === undefined ? undefined : toVisibleImpactReport(reportQuery.data)),
        [reportQuery.data],
    );

    const visibleAssetCount = report?.totalAssetCount ?? 0;

    const { isPending: isEnrolling, isSuccess: isEnrolled, isError: hasEnrollFailed, mutate } = enroll;

    const shouldAutoEnroll =
        reportQuery.data !== undefined && visibleAssetCount === 0 && !isEnrolled && !hasEnrollFailed;

    /**
     * The enroll write cannot be cancelled, so dismissing mid-flight is refused here — gated on the
     * mutation's own `isPending` rather than a copy of it in the store, which would lag a render behind.
     */
    const close = useCallback(() => {
        if (isEnrolling || shouldAutoEnroll) {
            return;
        }

        closeStore();
    }, [isEnrolling, shouldAutoEnroll, closeStore]);

    /**
     * The adapter drops assets the backend sent with no industry filter — a shape the FE believes
     * impossible. Reporting it here, keyed on the report's identity (so once per fetch), keeps the
     * pure adapter free of the tracker and off `select`, which may run more than once.
     */
    const droppedNotImpactedCount = reportQuery.data?.droppedNotImpactedCount ?? 0;

    useEffect(() => {
        if (droppedNotImpactedCount > 0) {
            trackError(
                new SiError(`Impact report returned ${droppedNotImpactedCount} asset(s) with no industry filter`, {
                    category: "api",
                    component: "IndustryTransitionImpactReport",
                }),
            );
        }
    }, [droppedNotImpactedCount]);

    /**
     * Guarded by a ref rather than the rendered `isPending`: two clicks in one tick both read the
     * pre-render value, and starting a second account-wide migration is not recoverable.
     */
    const isEnrollInFlight = useRef(false);

    const runEnroll = useCallback(
        (source: "enroll" | "retry") => {
            if (isEnrollInFlight.current) {
                return;
            }

            isEnrollInFlight.current = true;

            if (source === "retry") {
                industryTrackingService.trackImpactReportEnrollRetry();
            } else {
                industryTrackingService.trackImpactReportEnroll();
            }

            mutate(undefined, {
                onSettled: () => {
                    isEnrollInFlight.current = false;
                },
            });
        },
        [mutate],
    );

    const startEnroll = useCallback(() => runEnroll("enroll"), [runEnroll]);

    const retryEnroll = useCallback(() => {
        markEnrollRetryUsed();
        runEnroll("retry");
    }, [markEnrollRetryUsed, runEnroll]);

    const { refetch } = reportQuery;
    const retryReport = useCallback(() => refetch(), [refetch]);

    useEffect(() => {
        if (!shouldAutoEnroll || isEnrolling || isEnrollInFlight.current) {
            return;
        }

        runEnroll("enroll");
    }, [shouldAutoEnroll, isEnrolling, runEnroll]);

    const phase = derivePhase({
        isEnrolling,
        isEnrolled,
        hasEnrollFailed,
        hasReportLoaded: reportQuery.data !== undefined,
        visibleAssetCount,
        hasReportFailed: reportQuery.isError,
    });

    return {
        phase,
        report,
        canEnroll: phase === impactReportPhaseMap.ready,
        canRetryEnroll: !hasUsedEnrollRetry,
        handlers: { enroll: startEnroll, retryEnroll, retryReport, close },
    };
};

type PhaseInput = {
    readonly isEnrolling: boolean;
    readonly isEnrolled: boolean;
    readonly hasEnrollFailed: boolean;
    readonly hasReportLoaded: boolean;
    readonly visibleAssetCount: number;
    readonly hasReportFailed: boolean;
};

/** A loaded report outranks a failed refetch, so a background failure never wipes what is on screen. */
const derivePhase = ({
    isEnrolling,
    isEnrolled,
    hasEnrollFailed,
    hasReportLoaded,
    visibleAssetCount,
    hasReportFailed,
}: PhaseInput): ImpactReportPhase => {
    if (isEnrolling) {
        return impactReportPhaseMap.enrolling;
    }

    if (isEnrolled) {
        return impactReportPhaseMap.success;
    }

    if (hasEnrollFailed) {
        return impactReportPhaseMap.enrollError;
    }

    if (hasReportLoaded && visibleAssetCount === 0) {
        return impactReportPhaseMap.enrolling;
    }

    if (hasReportLoaded && visibleAssetCount > 0) {
        return impactReportPhaseMap.ready;
    }

    return hasReportFailed ? impactReportPhaseMap.loadError : impactReportPhaseMap.loading;
};
